import { FeedbackSendPageService } from './feedback.send.page.service';
import { SliderCaptchaComponent } from '../../components/slider-captcha.comonent';
import { activationClasses } from '../../common/consts';
import { ServerHandler } from '../../common/server/serverHandler';

export class FeedbackSendPage {
  // Настройки ограничений
  private readonly MAX_FILES = 5;
  private readonly MAX_SIZE_MB = 2;
  private readonly MAX_SIZE_BYTES = this.MAX_SIZE_MB * 1024 * 1024;
  private sliderCaptcha: SliderCaptchaComponent | null = null;
  private captchaPassed: boolean;
  // Разрешенные расширения файлов
  private readonly ALLOWED_EXTENSIONS = [
    '.txt', '.doc', '.docx', '.pdf', '.odt', '.md', '.json',
    '.jpg', '.jpeg', '.png', '.tiff', '.bmp', '.svg', '.webp',
    '.html', '.htm', '.xls', '.xlsx',
    '.mp3', '.wav', '.mp4', '.avi', '.mkv',
    '.log', '.ini', '.conf', '.yaml',
    '.zip', '.rar', '.7z', '.tar', '.gz', '.gzip', '.xz', '.txz', '.dmg', '.bz2', '.bzip2', '.cab',
  ];

  private feedbackTextarea: HTMLInputElement | null;
  private feedbackSendPageService: FeedbackSendPageService;
  private sidePopupClose: HTMLElement | null;
  private selectedFiles: File[] = [];
  private fileList: HTMLElement | null;
  private fileManager: HTMLElement | null;
  private sliderCaptchaHint: HTMLElement | null;

  constructor(
    private container: HTMLFormElement,
    private loader: HTMLElement | null,
    private sidePopup: HTMLElement | null
  ) {
    this.feedbackSendPageService = new FeedbackSendPageService();
    this.feedbackTextarea = this.container.querySelector('[data-feedback-textarea]');
    this.sidePopupClose = this.sidePopup
      ? this.sidePopup.querySelector('[data-side-popup-close]')
      : null;
    this.fileList = document.getElementById('file-list');
    this.fileManager = document.getElementById('file-manager');
    this.sliderCaptchaHint = container.querySelector('[data-slider-captcha-hint]');
    this.captchaPassed = false;
  }

  public initialize(): void {
    const dropZone = document.getElementById('drop-zone');
    const fileInput = document.getElementById('fileInput') as HTMLInputElement;

    // Настройка валидации полей
    this.container.querySelectorAll('label').forEach((label: HTMLElement) => {
      const feedbackInput: HTMLInputElement | null = label.querySelector('[data-feedback-input]');

      if (!feedbackInput && !label.querySelector('[data-feedback-textarea]')) {
        label.addEventListener('click', () => {
          const container = label.closest('[data-feedback-wrap]') as HTMLElement | null;
          if (container) {
            container.blur();
          }
          const activeElement = document.activeElement as HTMLElement | null;
          if (activeElement) {
            activeElement.blur();
          }
        });
      }
      if (feedbackInput && feedbackInput.name === 'phone') {
        feedbackInput.addEventListener('input', (e: Event) => {
          const target = e.target as HTMLInputElement;
          if (target) {
            target.value = target.value.replace(/[^\d+()-]/g, '');
          }
        });
      }
    });

    // Настройка загрузки файлов
    if (dropZone && fileInput) {
      fileInput.accept = this.ALLOWED_EXTENSIONS.join(',');
      dropZone.onclick = () => fileInput.click();
      fileInput.onchange = (e: any) => {
        this.addFiles(e.target.files);
        fileInput.value = '';
      };
      dropZone.ondragover = (e) => {
        e.preventDefault();
        dropZone.classList.add('file-downloader__drop-zone--over');
      };
      dropZone.ondragleave = () => dropZone.classList.remove('file-downloader__drop-zone--over');
      dropZone.ondrop = (e) => {
        e.preventDefault();
        dropZone.classList.remove('file-downloader__drop-zone--over');
        if (e.dataTransfer) this.addFiles(e.dataTransfer.files);
      };
    }

    // Инициализация слайдер-каптчи (новая)
    const captchaContainer = this.container.querySelector('[data-slider-captcha]');
    const viewMessage = captchaContainer?.querySelector('h6');
    if (captchaContainer) {
      this.sliderCaptcha = new SliderCaptchaComponent(
        captchaContainer as HTMLElement,
        this.feedbackSendPageService,
        () => {
          // Каптча пройдена

          this.loader?.classList.add(activationClasses.active);
          this.feedbackSendPageService.verifyCaptcha(
            new FormData(),
          ).then((response: Response | null) => {
            if (response) {
              this.captchaPassed = ServerHandler.successAnswer(response.status);
              if (viewMessage) {
                viewMessage.textContent = '';
              }
              return response.json();
            }
          }).then((data: { message?: string }) => {
            if (!this.captchaPassed) {
              if (viewMessage && data.message) {
                viewMessage.textContent = data.message;
              }
              if (this.sliderCaptcha) {
                this.sliderCaptcha.loadNewCaptcha();
              }
            } else {
              if (this.sliderCaptchaHint && data.message === 'success') {
                this.sliderCaptchaHint.innerText = ''; // Капча пройдена
                this.sliderCaptchaHint.classList.add('green');
                if (this.sliderCaptcha) {
                  this.sliderCaptcha.lockCaptchaExternally();
                }
              }
            }
          }).finally(() => {
            this.loader?.classList.remove(activationClasses.active);
          });
        },
        (errorMessage) => {
          this.showError(errorMessage);
        }
      );
    }
    this.initUrlParams();
    this.initCloseEvents();
    this.container.addEventListener('submit', async (event: Event) => {
      event.preventDefault();

      this.submitForm();
    });
  }

  private async submitForm(): Promise<void> {

    const isValid = this.validateForm();
    if (!isValid) {
      return;
    }

    await this.feedbackSendPageService.sendFeedback(
      this.container,
      this.sidePopup,
      this.loader,
      this.selectedFiles
    );
    if (this.feedbackSendPageService.isSuccess()) {
      this.selectedFiles = [];
      this.renderFiles();
      this.sliderCaptcha?.refreshCaptcha();
    }
  }

  private randomCaptcha() {
    return 'cap_' + Math.random().toString(36).substring(2, 11);
  }

  private getFileExtension(filename: string): string {
    return filename.substring(filename.lastIndexOf('.')).toLowerCase();
  }

  private isFileExtensionAllowed(filename: string): boolean {
    const extension = this.getFileExtension(filename);
    return this.ALLOWED_EXTENSIONS.includes(extension);
  }

  private addFiles(files: FileList): void {
    const filesArray = Array.from(files);

    for (const file of filesArray) {
      if (this.selectedFiles.length >= this.MAX_FILES) {
        this.showError(`Максимальное количество файлов: ${this.MAX_FILES}`);
        break;
      }

      if (file.size > this.MAX_SIZE_BYTES) {
        this.showError(`Файл "${file.name}" слишком большой. Максимальный размер: ${this.MAX_SIZE_MB}MB`);
        continue;
      }

      if (!this.isFileExtensionAllowed(file.name)) {
        this.showError(`Файл "${file.name}" имеет недопустимый тип. Разрешенные типы: ${this.ALLOWED_EXTENSIONS.join(', ')}`);
        continue;
      }

      const isDuplicate = this.selectedFiles.some(f => f.name === file.name && f.size === file.size);
      if (!isDuplicate) {
        this.selectedFiles.push(file);
      }
    }

    this.renderFiles();
  }

  private removeFile(index: number): void {
    this.selectedFiles.splice(index, 1);
    this.renderFiles();
  }

  private renderFiles(): void {
    if (!this.fileList || !this.fileManager) return;

    this.fileList.innerHTML = '';
    this.fileManager.style.display = this.selectedFiles.length ? 'block' : 'none';

    this.selectedFiles.forEach((file, index) => {
      const item = document.createElement('div');
      item.className = 'file-downloader__file-item';
      const fileSize = this.formatBytes(file.size);

      item.innerHTML = `
                <div class="file-downloader__file-info">
                    <span class="file-downloader__file-name">${this.escapeHtml(file.name)}</span>
                    <span class="file-downloader__file-size">(${fileSize})</span>
                </div>
                <svg width="11" height="11" viewBox="0 0 11 11" fill="none" class="file-downloader__delete-icon" 
                data-file-downloader-delete>
                    <path fill-rule="evenodd" clip-rule="evenodd" d="M5.50626 6.98063L9.21068 10.6808C9.40724 10.8772 
                    9.67385 10.9875 9.95184 10.9875C10.2298 10.9875 10.4964 10.8772 10.693 10.6808C10.8896 10.4845 11 
                    10.2182 11 9.94051C11 9.66283 10.8896 9.39653 10.693 9.20019L6.98719
                      5.5L10.6923 1.79981C10.7896 1.70259 10.8667 1.58718 10.9194 1.46018C10.972 1.33317 10.9991 
                      1.19705 10.9991 1.0596C10.999 0.922139
                       10.9719 0.786034 10.9192 0.659052C10.8665 0.532071 10.7893 0.416699 10.692 0.319526C10.5946 
                       0.222352 10.4791 0.145278 10.3519 0.0927054C10.2248 0.0401327 10.0885 0.0130905 9.95089 
                       0.0131229C9.81328 0.0131553 9.67702 0.0402615 9.54989 0.092894C9.42277 0.145527 9.30726 
                       0.222655 9.20998 0.319875L5.50626 4.02006L1.80185 0.319875C1.70524 0.219866 1.58966 0.140078 
                       1.46184 0.0851657C1.33403 0.0302535 1.19655 0.00131658 1.05742 4.38913e-05C0.918293 -0.0012288 
                       0.780303 0.0251879 0.651505 0.0777528C0.522707 0.130318 0.405679 0.207978 0.30725 
                       0.306202C0.208821 0.404427 0.130962 0.521248 0.0782157 0.64985C0.0254694 
                       0.778451 -0.00110759 0.916258 3.53584e-05 1.05523C0.00117831 1.1942 0.0300182 
                       1.33155 0.0848726 1.45927C0.139727 1.58699 0.219497 1.70252 0.319528 1.79911L4.02534 
                       5.5L0.320227 9.20089C0.220196 9.29748 0.140426 9.41301 0.0855714 9.54073C0.0307171 
                       9.66845 0.00187681 9.8058 0.000733858 9.94477C-0.000409094 10.0837 0.0261679 10.2215 
                       0.0789142 10.3502C0.13166 10.4788 0.20952 10.5956 0.307949 10.6938C0.406378 10.792 
                       0.523406 10.8697 0.652204 10.9222C0.781002 10.9748 0.918991 11.0012 1.05812 11C1.19725 
                       10.9987 1.33473 10.9697 1.46254 10.9148C1.59035 10.8599 1.70594 10.7801 1.80255 10.6801L5.50626 
                       6.98133V6.98063Z" fill="#8B8B8B"/>
                </svg>
            `;

      const deleteIcon = item.querySelector('[data-file-downloader-delete]');
      if (deleteIcon) {
        deleteIcon.addEventListener('click', () => this.removeFile(index));
      }

      this.fileList?.appendChild(item);
    });
  }

  private formatBytes(bytes: number, decimals = 2): string {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const dm = decimals < 0 ? 0 : decimals;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(dm)) + ' ' + sizes[i];
  }

  private escapeHtml(str: string): string {
    return str.replace(/[&<>]/g, function (m) {
      if (m === '&') return '&amp;';
      if (m === '<') return '&lt;';
      if (m === '>') return '&gt;';
      return m;
    });
  }

  private initUrlParams(): void {
    const params = new URLSearchParams(window.location.search);
    const connections = params.get('connections');
    const solution: string | null = params.get('solution');

    if (connections && solution && this.feedbackTextarea) {
      this.feedbackTextarea.value =
        `Количество соединений: ${connections}\nРешение: ${solution.replace(/плюс/gi, '+')}`;
    }
  }

  private initCloseEvents(): void {
    if (this.sidePopupClose) {
      this.sidePopupClose.addEventListener('click', () => {
        this.sidePopup?.classList.remove('active');
      });
    }
  }

  private validateForm(): boolean {
    const nameInput = this.container.querySelector('input[name="name"]') as HTMLInputElement;
    const phoneInput = this.container.querySelector('input[name="phone"]') as HTMLInputElement;
    const emailInput = this.container.querySelector('input[name="email"]') as HTMLInputElement;
    const companyInput = this.container.querySelector('input[name="company"]') as HTMLInputElement;
    const agreementInput = this.container.querySelector('input[name="agreement"]') as HTMLInputElement;

    if (!nameInput.value.trim()) {
      this.showError('Укажите ваше имя');
      nameInput.focus();
      return false;
    }

    if (!phoneInput.value.trim()) {
      this.showError('Укажите телефон');
      phoneInput.focus();
      return false;
    }

    if (!emailInput.value.trim()) {
      this.showError('Укажите email');
      emailInput.focus();
      return false;
    }

    if (!this.isValidEmail(emailInput.value.trim())) {
      this.showError('Укажите корректный email');
      emailInput.focus();
      return false;
    }

    if (!companyInput.value.trim()) {
      this.showError('Укажите название компании');
      companyInput.focus();
      return false;
    }

    if (!agreementInput.checked) {
      this.showError('Необходимо согласиться с обработкой персональных данных');
      return false;
    }

    return true;
  }

  private isValidEmail(email: string): boolean {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  }

  private showError(message: string): void {
    if (!this.sidePopup) {
      alert(message);
      return;
    }

    const titleElement = this.sidePopup.querySelector('h3');
    const textElement = this.sidePopup.querySelector('p');

    this.sidePopup.classList.add('active');

    if (titleElement) {
      titleElement.textContent = 'Ошибка';
    }

    if (textElement) {
      textElement.textContent = message;
    }
  }
}
